import { z } from "zod";
import { Timestamps } from "../../common/index.ts";
import {
  DocumentTypeId,
  FieldDefinitionId,
  FsmTranslationsSchema,
  CustomerId,
  TransitionId,
} from "../fsm.schema.ts";

// FieldDefinitionId is exported from fsm.schema.ts — re-exported via fsm/index.ts

export const FieldType = z.enum([
  "text",
  "number",
  "email",
  "date",
  "select",
  "multi_select",
  "textarea",
  "boolean",
  "file",
  "material_picker",
  "url",
  "url_list",
  "shipping_entry_list",
]);
export type FieldType = z.infer<typeof FieldType>;

export const SelectOption = z.object({
  value: z.string(),
  label: z.string(),
});
export type SelectOption = z.infer<typeof SelectOption>;

export const TextValidation = z.object({
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().positive().optional(),
  pattern: z.string().optional(),
});
export type TextValidation = z.infer<typeof TextValidation>;

export const NumberValidation = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
});
export type NumberValidation = z.infer<typeof NumberValidation>;

export const FieldValidation = z.union([
  TextValidation,
  NumberValidation,
  z.object({}),
]);
export type FieldValidation = z.infer<typeof FieldValidation>;

export const FieldDefinitionDto = z
  .object({
    id: FieldDefinitionId,
    customerId: CustomerId,
    documentTypeId: DocumentTypeId,
    name: z.string().min(1),
    label: z.string().min(1),
    fieldType: FieldType,
    sortOrder: z.number().int().nonnegative(),
    isRequired: z.boolean(),
    validation: z.record(z.string(), z.unknown()),
    options: z.array(SelectOption),
    defaultValue: z.unknown().nullable(),
    translations: FsmTranslationsSchema.nullable(),
    /** Transition IDs this field is scoped to. Empty = visible in all transitions. */
    scopedToTransitions: z.array(z.string()).default([]),
  })
  .merge(Timestamps);

export type FieldDefinitionDto = z.infer<typeof FieldDefinitionDto>;

export const CreateFieldDefinitionInput = z.object({
  customerId: CustomerId,
  documentTypeId: DocumentTypeId,
  name: z
    .string()
    .min(1)
    .regex(/^[a-z_][a-z0-9_]*$/, "name must be snake_case"),
  label: z.string().min(1),
  fieldType: FieldType,
  sortOrder: z.number().int().nonnegative().optional(),
  isRequired: z.boolean().optional(),
  validation: z.record(z.string(), z.unknown()).optional(),
  options: z.array(SelectOption).optional(),
  defaultValue: z.unknown().nullable().optional(),
  translations: FsmTranslationsSchema.optional(),
});
export type CreateFieldDefinitionInput = z.infer<
  typeof CreateFieldDefinitionInput
>;

export const UpdateFieldDefinitionInput = z.object({
  label: z.string().min(1).optional(),
  fieldType: FieldType.optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  isRequired: z.boolean().optional(),
  validation: z.record(z.string(), z.unknown()).optional(),
  options: z.array(SelectOption).optional(),
  defaultValue: z.unknown().nullable().optional(),
  translations: FsmTranslationsSchema.nullable().optional(),
});
export type UpdateFieldDefinitionInput = z.infer<
  typeof UpdateFieldDefinitionInput
>;

export const TransitionRequiredFieldsDto = z.object({
  transitionId: TransitionId,
  fieldDefinitionIds: z.array(FieldDefinitionId),
});
export type TransitionRequiredFieldsDto = z.infer<
  typeof TransitionRequiredFieldsDto
>;
